import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';

export default function RentVsBuyCalc() {
  const [monthlyRent, setMonthlyRent] = useState(1800);
  const [homePrice, setHomePrice] = useState(350000);
  const [downPayment, setDownPayment] = useState(20);
  const [rate, setRate] = useState(6.5);
  const [years, setYears] = useState(7);
  const [rentIncrease, setRentIncrease] = useState(3);
  const [appreciation, setAppreciation] = useState(3.5);

  const result = useMemo(() => {
    const loan = homePrice * (1 - downPayment / 100);
    const monthlyRate = rate / 100 / 12;
    const n = 30 * 12;
    let monthlyMortgage;
    if (monthlyRate === 0) monthlyMortgage = loan / n;
    else monthlyMortgage = (loan * monthlyRate * Math.pow(1 + monthlyRate, n)) / (Math.pow(1 + monthlyRate, n) - 1);

    const monthlyTaxIns = homePrice * 0.015 / 12;
    const totalBuyMonthly = monthlyMortgage + monthlyTaxIns;

    let totalRent = 0;
    let currentRent = monthlyRent;
    for (let y = 0; y < years; y++) {
      totalRent += currentRent * 12;
      currentRent *= (1 + rentIncrease / 100);
    }

    const totalBuy = totalBuyMonthly * 12 * years + homePrice * downPayment / 100;
    const futureValue = homePrice * Math.pow(1 + appreciation / 100, years);
    const equity = futureValue - loan * (1 - years / 30);
    const buyNetCost = totalBuy - equity;
    const recommendation = buyNetCost < totalRent ? 'buy' : 'rent';

    return { totalRent, totalBuy, futureValue, equity, buyNetCost, totalBuyMonthly, recommendation };
  }, [monthlyRent, homePrice, downPayment, rate, years, rentIncrease, appreciation]);

  const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);

  return (
    <Card className="bg-white rounded-3xl border border-stone-200 shadow-sm" data-testid="rent-vs-buy-calculator">
      <CardHeader className="pb-2">
        <CardTitle className="font-outfit text-xl font-bold text-stone-900">Rent vs. Buy</CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-xs font-bold uppercase tracking-widest text-emerald-900/70 mb-2 block">Monthly Rent</Label>
            <Input type="number" value={monthlyRent} onChange={(e) => setMonthlyRent(Number(e.target.value))} className="text-stone-900" data-testid="rvb-rent" />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-widest text-emerald-900/70 mb-2 block">Home Price</Label>
            <Input type="number" value={homePrice} onChange={(e) => setHomePrice(Number(e.target.value))} className="text-stone-900" data-testid="rvb-price" />
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label className="text-xs font-bold uppercase tracking-widest text-emerald-900/70 mb-2 block">Down (%)</Label>
            <Input type="number" value={downPayment} onChange={(e) => setDownPayment(Number(e.target.value))} className="text-stone-900" data-testid="rvb-down" />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-widest text-emerald-900/70 mb-2 block">Rate (%)</Label>
            <Input type="number" step="0.1" value={rate} onChange={(e) => setRate(Number(e.target.value))} className="text-stone-900" data-testid="rvb-rate" />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-widest text-emerald-900/70 mb-2 block">Years</Label>
            <Input type="number" value={years} onChange={(e) => setYears(Number(e.target.value))} className="text-stone-900" data-testid="rvb-years" />
          </div>
        </div>
        <div>
          <Label className="text-xs font-bold uppercase tracking-widest text-emerald-900/70 mb-2 block">Time Horizon: {years} years</Label>
          <Slider value={[years]} onValueChange={(v) => setYears(v[0])} min={1} max={30} step={1} className="mt-1" />
        </div>
        <div className={`rounded-2xl p-4 space-y-2 ${result.recommendation === 'buy' ? 'bg-emerald-50' : 'bg-amber-50'}`}>
          <div className="text-center mb-2">
            <span className={`font-outfit font-bold text-lg ${result.recommendation === 'buy' ? 'text-emerald-900' : 'text-amber-900'}`} data-testid="rvb-recommendation">
              {result.recommendation === 'buy' ? 'Buying is Better' : 'Renting is Better'}
            </span>
            <span className="block text-xs text-stone-500">over {years} years</span>
          </div>
          <div className="flex justify-between">
            <span className="text-stone-600 text-sm">Total Rent Cost</span>
            <span className="font-semibold text-stone-700" data-testid="rvb-total-rent">{fmt(result.totalRent)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-stone-600 text-sm">Net Buy Cost</span>
            <span className="font-semibold text-stone-700" data-testid="rvb-net-buy">{fmt(result.buyNetCost)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-stone-600 text-sm">Est. Home Value</span>
            <span className="font-semibold text-stone-700">{fmt(result.futureValue)}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
